import { Injectable, inject } from '@angular/core';
import { AccountType, CompanyProfile, Profile, UsersService } from './bdoservice/users/users.service';
import { Subject, BehaviorSubject } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakRole } from './bdoservice/common/model/keycloak-role.model';
import { EmployeeProfile } from './bdoservice/users/models/employee-profile.model';
import { ActivatedPackage } from './bdoservice/users/models/activated-package.model';
import * as Sentry from "@sentry/angular-ivy";
import { DialogService } from 'primeng/dynamicdialog';
import { ProfileErrorDialogComponent } from '../common/components/profile-error-dialog/profile-error-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { DialogHelper } from '../common/helpers/dialog-helper';
import { isCompanyInvoiceFilled } from '../common/utils/company-invoice.utils';

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  private readonly usersService = inject(UsersService);
  private readonly keycloakService = inject(KeycloakService);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);

  private profile: Subject<ProfileState> = new BehaviorSubject<ProfileState>(null);
  public profile$ = this.profile.asObservable();

  private activePackage: Subject<ActivatedPackage | null> = new BehaviorSubject<ActivatedPackage | null>(null);
  public activePackage$ = this.activePackage.asObservable();

  private inactivePackages: Subject<ActivatedPackage[]> = new BehaviorSubject<ActivatedPackage[]>([]);
  public inactivePackages$ = this.inactivePackages.asObservable();

  getAccountType(): AccountType {
    return this.keycloakService.getUserRoles().includes(KeycloakRole.COMPANY) ? AccountType.COMPANY : AccountType.EMPLOYEE;
  }

  refreshPackage(){
    this.usersService.getProfile(this.getAccountType()).then(profile => {
      console.log("refreshing package");
      const accountType = this.getAccountType();
      this.activePackage.next(
        this.getActivePackage(profile, accountType)
      );
      this.inactivePackages.next(
        this.getInactivePackages(profile, accountType)
      );
    })
  }

  loadProfile(){
    this.profile.next({
      loading: true,
    });

    if (!this.keycloakService.isLoggedIn()) {
      this.profile.next({ loading: false });
      return;
    }

    this.keycloakService.loadUserProfile().then((profile) => {

      Sentry.setUser({
        email: profile.email,
        username: profile.username,
        id: profile.id,
      });

      if(profile['attributes'].marketingAgreement == null && this.keycloakService.getUserRoles().includes(KeycloakRole.COMPANY)){

        const emptyProfile = {
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
        } as Profile;



        this.profile.next(
          {
            loading: false,
            requireFirstConfiguration: true,
            requireCompanyData: false,
            data: emptyProfile,
          }
        );

        this.activePackage.next(null);
      } else {
        const accountType = this.keycloakService.getUserRoles().includes('COMPANY') ? AccountType.COMPANY : AccountType.EMPLOYEE;

        this.usersService.getProfile(accountType).then(profile => {
          // Validate profile response
          if (!this.isValidProfileResponse(profile)) {
            this.handleProfileError();
            return;
          }

          const requireCompanyData = accountType === AccountType.COMPANY
            && !isCompanyInvoiceFilled((profile as CompanyProfile).companyInvoice);

          this.profile.next(
            {
              loading: false,
              requireFirstConfiguration: false,
              requireCompanyData,
              data: this.buildProfile(profile, accountType),
            }
          );

          this.activePackage.next(
            this.getActivePackage(profile, accountType)
          );

          this.inactivePackages.next(
            this.getInactivePackages(profile, accountType)
          );

        }).catch(err => {
          console.error('Profile loading error:', err);
          this.handleProfileError();
        });
      }
    }).catch((err) => {
      console.error('Keycloak profile loading error:', err);
      this.handleProfileError();
    });
  }

  getActivePackage(profile: Profile, accountType: AccountType): ActivatedPackage | null {
    if(accountType === AccountType.COMPANY){
      const companyProfile = profile as CompanyProfile;
      const activePackage = companyProfile.packages?.find(x => x.activated);
      return activePackage;
    } else if(accountType === AccountType.EMPLOYEE){
      const employeeProfile = profile as EmployeeProfile;
      return employeeProfile.activatedPackage;
    } else {
      return null;
    }
  }

  getInactivePackages(profile: Profile, accountType: AccountType): ActivatedPackage[] {
    if(accountType === AccountType.COMPANY){
      const companyProfile = profile as CompanyProfile;
      const inactivePackages = companyProfile.packages?.filter(x => !x.activated) || [];
      return inactivePackages;
    } else {
      // Employees don't have multiple packages
      return [];
    }
  }

  private buildProfile(profile: Profile, accountType: AccountType): Profile{
    switch(accountType){
      case AccountType.COMPANY:
        return this.getCompanyProfile(profile);
      case AccountType.EMPLOYEE:
        return this.getEmployeeProfile(profile);

    }
  }

  private getEmployeeProfile(profile: Profile): EmployeeProfile{
    const originalProfile = profile as EmployeeProfile;
        return new EmployeeProfile(
          originalProfile.username,
          originalProfile.email,
          originalProfile.firstName,
          originalProfile.lastName,
          originalProfile.companyId,
          originalProfile.companyName,
          originalProfile.activatedPackage,
          originalProfile.credentials,
        )
  }

  private getCompanyProfile(profile: Profile): CompanyProfile{
    const originalProfile = profile as CompanyProfile;
        return new CompanyProfile(
          originalProfile.username,
          originalProfile.email,
          originalProfile.firstName,
          originalProfile.lastName,
          originalProfile.companyName,
          originalProfile.packages,
          originalProfile.mainAccountCreated,
          originalProfile.credentials,
          originalProfile.marketingAgreement,
          originalProfile.companyInvoice,
          originalProfile.personInvoice,
          originalProfile.authorities,
        )
  }

  private isValidProfileResponse(profile: unknown): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const profileAny = profile as any;
    return !!(profile && profileAny?.username);
  }

  private handleProfileError(): void {
    this.profile.next({
      loading: false,
      error: new Error('Profile loading failed'),
    });

    this.dialogService.open(ProfileErrorDialogComponent, DialogHelper.createNarrowConfig({
      header: this.translate.instant('profile.error.title'),
      closable: false,
      data: {
        message: this.translate.instant('profile.error.defaultMessage'),
        onRetry: () => this.loadProfile()
      }
    }));
  }
}

interface ProfileState {
  loading: boolean;
  requireFirstConfiguration?: boolean | null;
  requireCompanyData?: boolean | null;
  error?: Error | null;
  data?: Profile;
}
